import { styled } from '@mui/material/styles';
import { Box, IconButton, Typography, Button } from '@mui/material';

export const StyledContainer = styled(Box)<{ isSidebarOpen?: boolean }>(({ theme, isSidebarOpen }) => ({
  backgroundColor: '#ffffff',
  minHeight: '72px',
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  padding: '16px 24px',
  transition: 'all 0.3s ease',
  width: '100%',
  position: 'relative',
  zIndex: 1200,
  borderBottom: '1px solid #E7EFF5',
  [theme.breakpoints.down('md')]: {
    padding: '12px 16px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 12px',
    gap: '8px',
  }
}));

export const MenuButton = styled(Box)<{ $isOpen?: boolean }>(({ $isOpen }) => ({
  background: '#E7EFF5',
  borderRadius: '8px',
  padding: '8px',
  width: '45px',
  height: '45px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '& svg': {
    transition: 'transform 0.3s ease',
    transform: $isOpen ? 'rotate(180deg)' : 'rotate(0)'
  }
}));

export const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  alignItems: 'center',
  flex: 1,
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
    gap: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '8px',
  }
}));

export const TitleSection = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  alignItems: 'center',
  padding: '0 16px 0 0',
});

export const BreadcrumbChip = styled(Box)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '28px',
  borderRadius: '4px',
  padding: '4px 12px',
  marginRight: '12px',
  backgroundColor: '#00B6BA',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '& .MuiTypography-root': {
    fontSize: '12px',
    fontWeight: 600,
    color: '#FFFFFF',
    lineHeight: 1,
  },
  '&:hover': {
    backgroundColor: '#009B9F',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
}));

export const Title = styled(Typography)({
  color: '#063142',
  fontFamily: '"OpenSansCondensed-Bold", sans-serif',
  fontSize: '24px',
  lineHeight: '24px',
  fontWeight: 700,
});

export const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  alignItems: 'center',
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    gap: '8px',
    width: '100%',
    justifyContent: 'flex-start',
    '& > *': {
      flexGrow: 0,
    }
  },
  [theme.breakpoints.down('sm')]: {
    '& .secondary-filter': {
      display: 'none'
    },
    justifyContent: 'center'
  }
}));

export const FilterButton = styled(Button)({
  background: '#E7EFF5',
  borderRadius: '4px',
  border: '1px solid #063142',
  padding: '8px 10px',
  height: '30px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: '#063142',
  textTransform: 'none',
  '& .label': {
    fontFamily: '"Lato-Regular", sans-serif',
    fontSize: '12px',
    fontWeight: 400,
  },
});

export const StyledSegmentedControl = styled(Box)(({ theme }) => ({
  borderRadius: '4px',
  border: '1px solid #00B6BA',
  display: 'flex',
  flexDirection: 'row',
  gap: '0px',
  alignItems: 'center',
  justifyContent: 'center',
  height: '30px',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    flex: '1 0 auto'
  }
}));

export const StyledSegmentButton = styled(Button)<{ active?: boolean }>(({ active, theme }) => ({
  '&.MuiButton-root.MuiButton-root': {
    border: '1px solid #00B6BA',
    padding: '12px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: `${active ? '#00B6BA !important' : 'transparent !important'}`,
    color: active ? '#ffffff !important' : '#00B6BA !important',
    borderRadius: 0,
    minWidth: 'auto',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: `${active ? '#00B6BA !important' : 'rgba(0, 182, 186, 0.1) !important'}`,
      color: active ? '#ffffff !important' : '#00B6BA !important',
    },
    '&:focus': {
      outline: 'none',
      color: active ? '#ffffff !important' : '#00B6BA !important',
    }
  },
  '&:last-child': {
    borderRight: 'none'
  }
}));

export const StyledActionButton = styled(Button)(({ theme }) => ({
  background: '#E7EFF5',
  borderRadius: '1000px',
  padding: '0 24px',
  height: '30px',
  color: '#063142',
  '& .MuiTypography-root': {
    fontFamily: '"Lato-Bold", sans-serif',
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minWidth: 'auto',
    '& .button-text': {
      display: 'none'
    },
    '& .button-icon': {
      display: 'block'
    }
  }
}));

export const StyledBookmarkButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '8px 10px',
  height: '40px',
  color: '#00ACE7',
  whiteSpace: 'nowrap',
  '& .MuiTypography-root': {
    fontFamily: '"Lato-Bold", sans-serif',
    fontSize: '14px',
    fontWeight: 700,
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

export const BackButton = styled(IconButton)({
  padding: '4px',
  marginRight: '8px',
});

export const TabButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  textTransform: 'none',
  minHeight: '30px',
  padding: '0 16px',
  borderRadius: '4px',
  border: '1px solid #063142',
  backgroundColor: active ? '#E7EFF5' : 'transparent',
  color: '#063142',
  '&:hover': {
    backgroundColor: active ? '#E7EFF5' : 'rgba(231, 239, 245, 0.4)',
  },
  whiteSpace: 'nowrap',
  fontSize: '14px',
  fontWeight: 400,
}));

export const MobileFilterButton = styled(Button)(({ theme }) => ({
  display: 'none',
  backgroundColor: '#E7EFF5',
  borderRadius: '1000px',
  padding: '0 16px',
  height: '30px',
  color: '#00B6BA',
  border: '1px solid #00B6BA',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  }
}));

export const MobileFilterDrawer = styled(Box)<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1300,
  opacity: isOpen ? 1 : 0,
  visibility: isOpen ? 'visible' : 'hidden',
  transition: 'opacity 0.3s ease, visibility 0.3s ease',
}));

export const MobileFilterContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  backgroundColor: '#ffffff',
  borderTopLeftRadius: '16px',
  borderTopRightRadius: '16px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  transform: 'translateY(0)',
  transition: 'transform 0.3s ease',
  maxHeight: '80vh',
  overflow: 'auto',
}));

export const MobileFilterHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  borderBottom: '1px solid #E7EFF5',
  paddingBottom: '8px',
});

export const MobileFilterActions = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
  paddingTop: '8px',
  borderTop: '1px solid #E7EFF5',
}); 